import { useEffect, useState } from 'react';
import {
  AQF_LEVEL_LABELS,
  AQF_LEVELS,
  AU_STATES,
  COURSE_CATEGORIES,
  FEE_BRACKETS,
  IELTS_MAX_OPTIONS,
  INTAKE_MONTHS,
} from '@admitly/shared';
import { cn } from '../../lib/cn.js';
import { useCategoryLabelResolver } from '../../hooks/useCategories.js';
import { Input } from '../ui/Input.js';
import { listIncludes, toggleInList, type PatchFn, type RawSearch } from './types.js';

type Props = {
  search: RawSearch;
  onPatch: PatchFn;
};

function Chip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        'px-2.5 py-1 rounded-full text-xs font-medium border transition',
        active
          ? 'bg-navy text-cream border-navy'
          : 'bg-white text-ink-soft border-line hover:border-navy/40 hover:text-navy',
      )}
    >
      {children}
    </button>
  );
}

function Group({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-[0.1em] text-muted-soft font-semibold mb-1.5">
        {label}
      </div>
      <div className="flex flex-wrap gap-1.5">{children}</div>
    </div>
  );
}

export function FilterChips({ search, onPatch }: Props) {
  const { categoryLabel } = useCategoryLabelResolver();
  // ANZSCO is a free-text code; debounce so each keystroke doesn't re-navigate.
  const [anzsco, setAnzsco] = useState(search.anzsco ?? '');
  useEffect(() => {
    setAnzsco(search.anzsco ?? '');
  }, [search.anzsco]);
  useEffect(() => {
    const id = setTimeout(() => {
      const next = anzsco.trim();
      if (next !== (search.anzsco ?? '').trim()) onPatch({ anzsco: next || undefined });
    }, 400);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anzsco]);

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      <Group label="Category">
        {COURSE_CATEGORIES.map((c) => (
          <Chip
            key={c}
            active={listIncludes(search.category, c)}
            onClick={() => onPatch({ category: toggleInList(search.category, c) })}
          >
            {categoryLabel(c)}
          </Chip>
        ))}
      </Group>

      <Group label="AQF level">
        {AQF_LEVELS.map((l) => (
          <Chip
            key={l}
            active={listIncludes(search.aqf, l)}
            onClick={() => onPatch({ aqf: toggleInList(search.aqf, l) })}
          >
            {AQF_LEVEL_LABELS[l].replace('AQF ', '').split(' — ')[0]}
          </Chip>
        ))}
      </Group>

      <Group label="State">
        {AU_STATES.map((s) => (
          <Chip
            key={s}
            active={listIncludes(search.state, s)}
            onClick={() => onPatch({ state: toggleInList(search.state, s) })}
          >
            {s}
          </Chip>
        ))}
      </Group>

      <Group label="Intake month">
        {INTAKE_MONTHS.map((m) => (
          <Chip
            key={m.value}
            active={listIncludes(search.intake, String(m.value))}
            onClick={() => onPatch({ intake: toggleInList(search.intake, String(m.value)) })}
          >
            {m.label}
          </Chip>
        ))}
      </Group>

      <Group label="Max IELTS">
        {IELTS_MAX_OPTIONS.map((band) => {
          const v = String(band);
          const active = search.ieltsMax === v;
          return (
            <Chip key={v} active={active} onClick={() => onPatch({ ieltsMax: active ? undefined : v })}>
              ≤ {band.toFixed(1)}
            </Chip>
          );
        })}
      </Group>

      <Group label="Annual fee">
        {FEE_BRACKETS.map((b) => {
          const active =
            search.feeMin === String(b.min) &&
            (b.max === null ? !search.feeMax : search.feeMax === String(b.max));
          return (
            <Chip
              key={b.key}
              active={active}
              onClick={() =>
                onPatch(
                  active
                    ? { feeMin: undefined, feeMax: undefined }
                    : { feeMin: String(b.min), feeMax: b.max === null ? undefined : String(b.max) },
                )
              }
            >
              {b.label}
            </Chip>
          );
        })}
      </Group>

      <Group label="PR pathway">
        <Chip active={search.pr === 'true'} onClick={() => onPatch({ pr: search.pr === 'true' ? undefined : 'true' })}>
          PR pathway only
        </Chip>
      </Group>

      <Group label="ANZSCO code">
        <Input
          value={anzsco}
          onChange={(e) => setAnzsco(e.target.value)}
          placeholder="e.g. 261313"
          className="h-9 w-32 text-sm"
          inputMode="numeric"
          aria-label="ANZSCO code"
        />
      </Group>
    </div>
  );
}
